import React, { useRef, useState } from 'react'
import { createStyles, withStyles } from '@mui/styles'
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton, Table, TableBody, TableCell, TableHead, 
         TableRow, Tooltip, Typography } from '@mui/material'
import { DeleteOutlined } from '@mui/icons-material'
import moment from 'moment'
import { muiOptions, MuiProps } from '../../infrastructure/materialUiThemeProvider'
import { UserSettingsContextContainer } from '../../infrastructure/shell/userSettings/userSettingsStore'
import { t } from '../../infrastructure/i18nextHelper'
import { Claims } from '../../infrastructure/signIn/models'
import { hasClaim } from '../../infrastructure/signIn/userContext'
import * as api from '../../infrastructure/api'
import { snackbars } from '../../infrastructure/snackbars'
import { Button, CustomDialog } from '../common/customComponents'
import { useSnapshotLabel } from './useSnapshotLabel'
import { MktSaleForecastSnapshotInfo, SnapshotType } from './models'

export type SnapshotListDialogArgs = {
    snapshotType: SnapshotType
    snapshots: MktSaleForecastSnapshotInfo[]
    displayCompanyColumn: boolean
    getCompanyName?: (companyCode: string) => string
    onSnapshotDeleted?: (snapshot: MktSaleForecastSnapshotInfo) => void | Promise<void>
}

export type SnapshotListDialogProps = {
    isOpen: boolean
    snapshotType: SnapshotType | null
    snapshots: MktSaleForecastSnapshotInfo[]
    displayCompanyColumn: boolean
    getCompanyName: (companyCode: string) => string
    onClose: () => void
    onDelete: (snapshot: MktSaleForecastSnapshotInfo) => Promise<void>
}

export let useSnapshotListDialog = () => {
    let [isOpen, setIsOpen] = useState<boolean>(false)
    let [snapshotType, setSnapshotType] = useState<SnapshotType | null>(null)
    let [snapshots, setSnapshots] = useState<MktSaleForecastSnapshotInfo[]>([])
    let [displayCompanyColumn, setDisplayCompanyColumn] = useState<boolean>(false)
    let getCompanyName = useRef<(companyCode: string) => string>(companyCode => companyCode)
    let onSnapshotDeleted = useRef<((snapshot: MktSaleForecastSnapshotInfo) => void | Promise<void>) | undefined>(undefined)
    let { getSnapshotLabel } = useSnapshotLabel()

    let open = (args: SnapshotListDialogArgs) => {
        setSnapshotType(args.snapshotType)
        setSnapshots(args.snapshots ?? [])
        setDisplayCompanyColumn(args.displayCompanyColumn)
        getCompanyName.current = args.getCompanyName ?? (companyCode => companyCode)
        onSnapshotDeleted.current = args.onSnapshotDeleted
        setIsOpen(true)
    }

    let close = () => setIsOpen(false)

    let deleteSnapshot = async (snapshot: MktSaleForecastSnapshotInfo) => {
        await api.del(`stock/movement/mktsale/snapshot/${snapshot.id}`)
        setSnapshots(current => current.filter(x => x.id !== snapshot.id))
        snackbars.success(t('mktSales.forecast.cancelForecastSuccess',
            { type: snapshotType ? getSnapshotLabel(snapshotType) : '' }))
        await onSnapshotDeleted.current?.(snapshot)
    }

    return {
        open,
        close,
        snapshots,
        dialogProps: {
            isOpen,
            snapshotType,
            snapshots,
            displayCompanyColumn,
            getCompanyName: getCompanyName.current,
            onClose: close,
            onDelete: deleteSnapshot
        }
    }
}

let formatDate = (date: string | null | undefined, dateFormat: string) =>
    !!date && moment(date).isValid() ? moment(date).format(dateFormat) : ''

function _SnapshotListDialog({ isOpen, snapshotType, snapshots, displayCompanyColumn, getCompanyName, onClose, onDelete, classes }
    : SnapshotListDialogProps & MuiProps) {
    let settings = UserSettingsContextContainer.useContainer()
    let { getSnapshotLabel } = useSnapshotLabel()
    let [snapshotToDelete, setSnapshotToDelete] = useState<MktSaleForecastSnapshotInfo | null>(null)

    let dateFormat = settings.getDateFormat()
    let snapshotLabel = snapshotType ? getSnapshotLabel(snapshotType) : ''
    let isManager = hasClaim(Claims.SalesManager)

    let confirmDelete = async () => {
        if (!snapshotToDelete) return
        let snapshot = snapshotToDelete
        setSnapshotToDelete(null)
        await onDelete(snapshot)
    }

    return (
        <>
            <Dialog open={isOpen} onClose={onClose} scroll='paper' maxWidth='sm'>
                <DialogTitle>{t('mktSales.forecast.snapshotListTitle', { type: snapshotLabel })}</DialogTitle>
                <DialogContent dividers>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                {displayCompanyColumn && <TableCell>{t('mktSales.forecast.company')}</TableCell>}
                                <TableCell>{t('mktSales.forecast.startDate')}</TableCell>
                                <TableCell>{t('mktSales.forecast.endDate')}</TableCell>
                                <TableCell>{t('mktSales.forecast.createdAt')}</TableCell>
                                {isManager && <TableCell className={classes.actionCell} />}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {snapshots.length === 0
                                ? <TableRow>
                                    <TableCell colSpan={(displayCompanyColumn ? 4 : 3) + (isManager ? 1 : 0)} align='center'>
                                        <Typography variant='body2'>
                                            {t('mktSales.forecast.noSnapshot', { type: snapshotLabel })}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                                : snapshots.map(snapshot =>
                                    <TableRow key={snapshot.id}>
                                        {displayCompanyColumn && <TableCell>{getCompanyName(snapshot.company)}</TableCell>}
                                        <TableCell>{formatDate(snapshot.start, dateFormat)}</TableCell>
                                        <TableCell>{formatDate(snapshot.end, dateFormat)}</TableCell>
                                        <TableCell>{formatDate(snapshot.createdAt, dateFormat)}</TableCell>
                                        {isManager && <TableCell className={classes.actionCell}>
                                            <Tooltip placement='top'
                                                title={<Typography variant='body1'>
                                                    {t('mktSales.forecast.cancelForecast', { type: snapshotLabel })}
                                                </Typography>}>
                                                <IconButton size='small'
                                                    onClick={() => setSnapshotToDelete(snapshot)}>
                                                    <DeleteOutlined />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>}
                                    </TableRow>)}
                        </TableBody>
                    </Table>
                </DialogContent>
                <DialogActions>
                    <Button label={t('history.close')} onClick={onClose} color='primary' />
                </DialogActions>
            </Dialog>

            <CustomDialog isOpen={!!snapshotToDelete}
                title={t('mktSales.forecast.cancelForecastDialogTitle', { type: snapshotLabel })}
                contentText={t('mktSales.forecast.cancelForecastDialogText', { type: snapshotLabel })}
                cancelButtonText={t('components.dialogClose')}
                confirmButtonText={t('mktSales.forecast.cancelForecast', { type: snapshotLabel })}
                onConfirm={confirmDelete}
                onClose={() => setSnapshotToDelete(null)}
                onCancel={() => setSnapshotToDelete(null)} />
        </>)
}

let styles = () => createStyles({
    actionCell: { width: '3em', textAlign: 'center' }
})

export let SnapshotListDialog = withStyles(styles, muiOptions)(_SnapshotListDialog)
